import {ServerConfig} from "@/config/config";
import {Axios} from "@/utils/axios";
import {advancedSearch} from "@/utils/browse/browse";
import {IGetResponse} from "@/utils/get-response";
import {HumanFilter} from "@/utils/human-filter";
import {IQueryParams, IQueryParamsWithOptionalView} from "@/utils/query-params";
import {View} from "@/models/view";
import {FetchedTotal} from "@/utils/views/fetched-total";
import { getModel } from "@/utils/models/model";
import {IExtraFieldQuickEditViaMenuDataResult} from "@/utils/models/extra-fields";
import {Project} from "@/models/base";
import ProjectExtraFields from "@/models/base/project-extra-fields";
import { ProjectAwsDocument } from "@/models/base/project-aws-document";

export class ProjectServiceClass {


	get url(): string {
		return ServerConfig.host + "/project";
	}



	async getSettings() {
		let result = await Axios.get(ServerConfig.host + "/browse/23", {
			params: {}
		});
		return result.data;
	}

	async get(id: number): Promise<Project> {
		let result = await Axios.get(this.url + "/" + id);
		return new Project(result.data);
	}

	async getTotals(view: View): Promise<FetchedTotal[]> {
		let result = await Axios.get(`${this.url}/view/${view.ID}/totals`);
		return (result.data || []).map((d: any) => new FetchedTotal(d));
	}

	async getProject(query: IQueryParamsWithOptionalView, extraFilters:HumanFilter[] = []): Promise<IGetResponse<Project>> {
		let result = await advancedSearch(query, this.url, getModel("Project"), extraFilters);
		result.data.data = result.data.data.map((s: any) => new Project(s));
		return result.data;
	}

	async search(queryString: string, query: IQueryParams): Promise<IGetResponse<Project>> {
		let result = await Axios.get(this.url + "/view/" + query.view.ID, {
			params: {
				limit: query.limit,
				offset: query.offset,
				order: query.orderBy,
				orderDirection: query.orderDirection,
				filters: [
					new HumanFilter({
						Field: "this.ID",
						Operator: "like",
						Values: [queryString]
					}),
				] as HumanFilter[]
			}
		});
		result.data.data = result.data.data.map((s: any) => new Project(s));
		return result.data;
	}

	async delete(project: Project[]): Promise<void> {
		await Axios.delete(this.url, {
			data: project.map(p => p.ID)
		});
	}

	async saveProject(project: Project): Promise<Project> {
		let result = await Axios.post(this.url, project, {});
		let p = new Project(result.data);
		for (let file of project.UnprocessedFiles){
			await this.uploadDocument(p, file);
		}
		for (let file of project.toDeleteFiles){
			await this.deleteDocument(p, file);
		}
		return p;
	}

	async putProject(project: Project): Promise<Project> {
		let result = await Axios.put(`${this.url}`, project.getJSON());
		for (let file of project.UnprocessedFiles){
			await this.uploadDocument(project, file);
		}
		for (let file of project.toDeleteFiles){
			await this.deleteDocument(project, file);
		}
		return new Project(result.data);
	}

	public async uploadDocument(project: Project, file: File): Promise<ProjectAwsDocument> {
		let data = new FormData();
		data.append("file", file, file.name);
		let result = await Axios.post(`${this.url}/${project.ID}/document`, data);
		return new ProjectAwsDocument(result.data);
	}

	public async deleteDocument(project: Project, document: ProjectAwsDocument): Promise<void> {
		await Axios.delete(`${this.url}/${project.ID}/document/${document.ID}`);
	}



	public async getExtraFields(project:Project | number):Promise<ProjectExtraFields>{
		if (typeof(project) == "object") {
			project = project.ID;
		}
		let result = await Axios.get(`${this.url}/${project}/extra-fields`);
		return new ProjectExtraFields(result.data);
	}

	public async updateExtraFields(project:Project, extraFields:ProjectExtraFields):Promise<ProjectExtraFields>{
		let result = await Axios.put(`${this.url}/extra-fields/${project.ExtraFieldsID}`, extraFields.getJSON());
		return new ProjectExtraFields(result.data);
	}

	public async batchEditExtraField(project:Project[], field:string, value:IExtraFieldQuickEditViaMenuDataResult) {
		let body = {
			ExtraFieldIDs: project.map(s=>s.ExtraFieldsID),
			Field: field,
			NumValue: value.numValue,
			TextValue: value.textValue,
			BoolValue: value.boolValue,
			TimeValue: value.timeValue
		};
		await Axios.post(`${this.url}/batch-edit-extra-field`, body);
	}

};


export const ProjectService = new ProjectServiceClass();